import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { KpiCard, ChartCard } from "@/components/dashboard/Cards";
import { fmtCurrency, fmtCurrencyShort, fmtMonth, fmtNum } from "@/lib/format";
import {
  stateChartColor, specialtyChartColor, chartColor,
  stateChartGradient, specialtyChartGradient, neutralChartGradient,
} from "@/lib/colorCoding";
import { useCustomization } from "@/context/CustomizationContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock, FileBarChart2 } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

const CAT_COLORS = {
  ar: "hsl(199 89% 33%)",
  coding: "hsl(173 58% 39%)",
  combined: "hsl(160 60% 45%)",
  payment_posting: "hsl(43 74% 56%)",
  patient_calling: "hsl(27 87% 57%)",
  pre_auth: "hsl(221 39% 41%)",
};
const CAT_LABELS = {
  ar: "AR", coding: "Coding", combined: "Combined RCM/Coding",
  payment_posting: "Payment Posting", patient_calling: "Patient Calling", pre_auth: "Pre-Auth",
};
const PIE_COLORS = ["hsl(199 89% 33%)", "hsl(173 58% 39%)", "hsl(160 60% 45%)", "hsl(43 74% 56%)", "hsl(27 87% 57%)", "hsl(221 39% 41%)"];
const NEUTRAL = chartColor(null);

const ChartTooltip = ({ active, payload, label, financial = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-white px-3 py-2 shadow-md text-xs">
      {label && <p className="font-semibold text-slate-900 mb-1">{typeof label === "string" && label.includes("-") ? fmtMonth(label) : label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-slate-600 tabular-nums">
          <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-medium">
            {financial ? fmtCurrency(p.value, 0) : fmtNum(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
};

const FteTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-slate-900 mb-1">{fmtMonth(label)}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-600 tabular-nums">
          <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: <span className="font-medium">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const DistTooltip = ({ active, payload, metric }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg border bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-slate-900">{p.payload.name}</p>
      <p className="text-slate-600 tabular-nums mt-0.5">
        <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: p.payload.color || p.fill }} />
        {metric === "revenue" ? fmtCurrency(p.value, 0) : `${p.value} FTEs`}
      </p>
    </div>
  );
};

// take the top N entries by the selected metric and group the rest into "Other"
const topN = (list, metric, n) => {
  const sorted = [...(list || [])]
    .filter((d) => (d[metric] || 0) > 0)
    .sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const data = sorted.slice(0, n).map((d) => ({ name: d.name, value: Math.round((d[metric] || 0) * 100) / 100 }));
  const other = sorted.slice(n).reduce((s, d) => s + (d[metric] || 0), 0);
  if (other > 0) data.push({ name: "Other", value: Math.round(other * 100) / 100 });
  return data;
};

const MetricToggle = ({ metric, onChange, idPrefix }) => (
  <div className="inline-flex rounded-lg border p-0.5 bg-white mb-1.5" data-testid={`${idPrefix}-metric-toggle`}>
    {["fte", "revenue"].map((m) => (
      <button
        key={m}
        data-testid={`${idPrefix}-metric-${m}`}
        onClick={() => onChange(m)}
        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-[background-color,color] duration-150 ${
          metric === m
            ? "bg-[hsl(var(--primary))] text-white"
            : "text-slate-500 hover:text-slate-800"
        }`}
      >
        {m === "fte" ? "FTEs" : "Revenue"}
      </button>
    ))}
  </div>
);

export default function DashboardPage() {
  const { colorMaps, summaryLabels, sectionLabels } = useCustomization();
  const detailsTitle = sectionLabels?.revenue_details || "Revenue Details";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [distMetric, setDistMetric] = useState("fte");
  const [expanded, setExpanded] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dashboard_expanded") || "{}");
    } catch {
      return {};
    }
  });

  const toggleSize = (key) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem("dashboard_expanded", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const load = () => {
      api.get("/dashboard")
        .then((res) => { setData(res.data); setError(null); })
        .catch(() => setError("Failed to load dashboard data."))
        .finally(() => setLoading(false));
    };
    load();
    // Refresh instantly whenever the user returns to this tab/window,
    // so any value changed elsewhere is reflected immediately.
    const onFocus = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }
  if (error) return <p className="text-sm text-red-600" data-testid="dashboard-error">{error}</p>;

  const {
    kpis, revenue_trend, category_mix, category_snapshot, top_clients,
    state_distribution = [], specialty_distribution = [],
  } = data;
  const financialAccess = data.financial_access !== false;
  const summaryConfig = data.summary_config || {
    categories: Object.keys(CAT_LABELS).map((key) => ({ key, label: CAT_LABELS[key] })),
    metrics: [
      { key: "invoice", label: "Invoice", financial: true },
      { key: "prorated", label: "Prorated FTE", financial: false },
      { key: "billable", label: "Billable FTE", financial: false },
    ],
  };
  const summaryMetrics = summaryConfig.metrics || [];
  const primaryMetric = summaryMetrics.find((item) => item.key === data.summary_primary_metric) ||
    summaryMetrics[0] || { key: "metric", label: "Metric", financial: false };
  const primaryKey = primaryMetric.key;
  const categoryDefs = summaryConfig.categories || [];
  const categoryKeys = categoryDefs.map((item) => item.key);
  const categoryLabel = (key) => categoryDefs.find((item) => item.key === key)?.label ||
    summaryLabels?.categories?.[key] || CAT_LABELS[key] || key;
  const metricLabel = (key) => summaryMetrics.find((item) => item.key === key)?.label || key;
  const fteMetrics = summaryMetrics.filter((item) => !item.financial);
  const hasData = revenue_trend.length > 0;
  const primaryChange = kpis.summary_metric_changes?.[primaryKey];
  const formatMetricValue = (definition, value) => {
    if (!financialAccess && definition.financial) return "$";
    if (value == null) return definition.financial ? "$" : "-";
    return definition.financial ? fmtCurrency(value, 0) : fmtNum(value);
  };
  const metric = financialAccess ? distMetric : "fte"; // masked roles: FTE only
  const isNeutralName = (n) => n === "Other" || n === "N/A";
  const stateData = topN(state_distribution, metric, 11).map((d) => ({
    ...d,
    color: isNeutralName(d.name) ? NEUTRAL : stateChartColor(d.name, colorMaps.state),
    gradient: isNeutralName(d.name) ? neutralChartGradient() : stateChartGradient(d.name, colorMaps.state),
  }));
  const specData = topN(specialty_distribution, metric, 10).map((d) => ({
    ...d,
    color: isNeutralName(d.name) ? NEUTRAL : specialtyChartColor(d.name, colorMaps.specialty),
    gradient: isNeutralName(d.name) ? neutralChartGradient() : specialtyChartGradient(d.name, colorMaps.specialty),
  }));

  const span = (key, base) => (expanded[key] ? "xl:col-span-12" : base);

  const distSubtitle = metric === "revenue"
    ? "Trailing 12 months revenue · multi-value engagements split equally"
    : "Active contracted FTEs · multi-value engagements split equally";

  const revenueCharts = [
    {
      key: "revenue-trend", testId: "chart-revenue-trend", base: "xl:col-span-7",
      title: `${metricLabel(primaryKey)} Trend`, subtitle: `Total ${metricLabel(primaryKey).toLowerCase()} per month`,
      render: (h) => (
        <ResponsiveContainer width="100%" height={h}>
          <AreaChart data={revenue_trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
            <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={40} />
            <YAxis tickFormatter={primaryMetric.financial ? fmtCurrencyShort : fmtNum} tick={{ fontSize: 11, fill: "#64748b" }} width={55} />
            <Tooltip content={<ChartTooltip financial={primaryMetric.financial} />} />
            <Area type="monotone" dataKey={primaryKey} name={metricLabel(primaryKey)} stroke="hsl(199 89% 33%)" strokeWidth={2}
              fill="hsl(199 89% 33%)" fillOpacity={0.12} />
          </AreaChart>
        </ResponsiveContainer>
      ),
    },
    {
      key: "category-snapshot", testId: "chart-category-snapshot", base: "xl:col-span-5",
      title: `${metricLabel(primaryKey)} Mix · ${fmtMonth(kpis.latest_month)}`, subtitle: "By service category",
      render: (h) => (
        <ResponsiveContainer width="100%" height={h}>
          <PieChart>
            <Pie data={category_snapshot} dataKey="value" nameKey="name" cx="50%" cy="50%"
              innerRadius={Math.round(h * 0.21)} outerRadius={Math.round(h * 0.34)} paddingAngle={2}>
              {category_snapshot.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<ChartTooltip financial={primaryMetric.financial} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
          </PieChart>
        </ResponsiveContainer>
      ),
    },
  ];

  const distributionCharts = [
    {
      key: "state-distribution", testId: "chart-state-distribution", base: "xl:col-span-5",
      title: "Business by State", subtitle: distSubtitle,
      render: (h) => {
        const hh = financialAccess ? h - 34 : h;
        return (
          <div>
            {financialAccess && <MetricToggle metric={metric} onChange={setDistMetric} idPrefix="state-dist" />}
            <ResponsiveContainer width="100%" height={hh}>
              <PieChart>
                <defs>
                  {stateData.map((d, i) => (
                    <linearGradient key={i} id={`state-grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={d.gradient.from} />
                      <stop offset="100%" stopColor={d.gradient.to} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie data={stateData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={Math.round(hh * 0.20)} outerRadius={Math.round(hh * 0.33)} paddingAngle={2}>
                  {stateData.map((d, i) => (
                    <Cell key={i} fill={`url(#state-grad-${i})`} stroke="white" strokeWidth={1} />
                  ))}
                </Pie>
                <Tooltip content={<DistTooltip metric={metric} />} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9}
                  payload={stateData.map((d) => ({ value: d.name, type: "circle", color: d.color }))} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );
      },
    },
    {
      key: "specialty-distribution", testId: "chart-specialty-distribution", base: "xl:col-span-7",
      title: "Business by Specialty", subtitle: distSubtitle,
      render: (h) => {
        const hh = financialAccess ? h - 34 : h;
        return (
          <div>
            {financialAccess && <MetricToggle metric={metric} onChange={setDistMetric} idPrefix="specialty-dist" />}
            <ResponsiveContainer width="100%" height={hh}>
              <BarChart data={specData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                <defs>
                  {specData.map((d, i) => (
                    <linearGradient key={i} id={`spec-grad-${i}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={d.gradient.from} />
                      <stop offset="100%" stopColor={d.gradient.to} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={metric === "revenue" ? fmtCurrencyShort : undefined} />
                <YAxis type="category" dataKey="name" width={150}
                  tick={{ fontSize: 10, fill: "#475569" }}
                  tickFormatter={(v) => (v.length > 22 ? v.slice(0, 21) + "…" : v)} />
                <Tooltip content={<DistTooltip metric={metric} />} />
                <Bar dataKey="value" name={metric === "revenue" ? "Revenue" : "FTEs"} radius={[0, 4, 4, 0]} barSize={14}>
                  {specData.map((d, i) => (
                    <Cell key={i} fill={`url(#spec-grad-${i})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      },
    },
  ];

  const compositionCharts = [
    {
      key: "category-mix", testId: "chart-category-mix", base: "xl:col-span-7",
      title: "Revenue by Category", subtitle: "Last 12 months composition",
      render: (h) => (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={category_mix} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
            <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: "#64748b" }} minTickGap={20} />
            <YAxis tickFormatter={primaryMetric.financial ? fmtCurrencyShort : fmtNum} tick={{ fontSize: 11, fill: "#64748b" }} width={55} />
            <Tooltip content={<ChartTooltip financial={primaryMetric.financial} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
            {categoryKeys.map((k, i) => (
              <Bar key={k} dataKey={k} stackId="rev" name={categoryLabel(k)}
                fill={CAT_COLORS[k] || PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ),
    },
    {
      key: "top-clients", testId: "chart-top-clients", base: "xl:col-span-5",
      title: `Top Clients by ${metricLabel(primaryKey)}`, subtitle: "Trailing 12 months",
      render: (h) => (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={top_clients} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" horizontal={false} />
            <XAxis type="number" tickFormatter={primaryMetric.financial ? fmtCurrencyShort : fmtNum} tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis type="category" dataKey="client" width={140}
              tick={{ fontSize: 10, fill: "#475569" }}
              tickFormatter={(v) => (v.length > 22 ? v.slice(0, 21) + "…" : v)} />
            <Tooltip content={<ChartTooltip financial={primaryMetric.financial} />} />
            <Bar dataKey={primaryKey} name={metricLabel(primaryKey)} fill="hsl(173 58% 39%)" radius={[0, 4, 4, 0]} barSize={16} />
          </BarChart>
        </ResponsiveContainer>
      ),
    },
  ];

  const fteChart = {
    key: "fte-trend", testId: "chart-fte-trend", base: "xl:col-span-12",
    title: "Summary Metric Trend", subtitle: "Non-financial metrics per month",
    render: (h) => (
      <ResponsiveContainer width="100%" height={h}>
        <LineChart data={revenue_trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
          <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: "#64748b" }} minTickGap={40} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={40} />
          <Tooltip content={<FteTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
          {fteMetrics.map((metric, index) => (
            <Line key={metric.key} type="monotone" dataKey={metric.key} name={metric.label}
              stroke={index ? "hsl(173 58% 39%)" : "hsl(199 89% 33%)"}
              strokeWidth={2} strokeDasharray={index ? "5 4" : undefined} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    ),
  };

  const primaryMetricVisible = financialAccess || !primaryMetric.financial;
  const charts = [
    ...(financialAccess ? revenueCharts : []),
    ...distributionCharts,
    ...(primaryMetricVisible ? compositionCharts : []),
    fteChart,
  ];

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <div className="flex justify-end">
        <Button asChild size="sm" className="rounded-lg" data-testid="dashboard-report-builder-button">
          <Link to="/report-builder">
            <FileBarChart2 className="h-4 w-4 mr-1.5" /> Report Builder
          </Link>
        </Button>
      </div>

      {!financialAccess && (
        <div className="rounded-xl border bg-slate-50 px-4 py-3 text-xs text-slate-500 flex items-center gap-2"
          data-testid="dashboard-financial-restricted-note">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          Financial values are restricted for your access level. FTE and engagement analytics are shown instead.
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {summaryMetrics.map((definition, index) => (
          <KpiCard
            key={definition.key}
            testId={`kpi-summary-${definition.key}`}
            label={`${definition.label} · ${fmtMonth(kpis.latest_month) || "-"}`}
            value={formatMetricValue(definition, kpis.summary_metrics?.[definition.key])}
            delta={definition.key === primaryKey ? primaryChange : undefined}
            sub={definition.key === primaryKey ? "vs previous month" : undefined}
            accent={index ? "sky" : "teal"}
          />
        ))}
        <KpiCard testId="kpi-active-engagements" label="Active Engagements" value={kpis.active_engagements}
          sub={`${fmtNum(kpis.active_fte)} contracted FTEs`} accent="emerald" />
        <KpiCard testId="kpi-active-clients" label="Active Clients" value={kpis.active_clients}
          sub="with running engagements" accent="amber" />
      </div>

      {/* Charts */}
      {hasData ? (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          {charts.map((c) => (
            <ChartCard
              key={c.key}
              testId={c.testId}
              title={c.title}
              subtitle={c.subtitle}
              className={span(c.key, c.base)}
              isExpanded={!!expanded[c.key]}
              onToggleSize={() => toggleSize(c.key)}
              render={c.render}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border bg-white shadow-[var(--app-shadow-sm)] py-20 text-center" data-testid="dashboard-empty">
          <p className="text-sm font-medium text-slate-700">No revenue data in this workspace yet</p>
          <p className="text-xs text-slate-500 mt-1">Add line items on the {detailsTitle} page to see charts and KPIs here.</p>
        </div>
      )}
    </div>
  );
}
